import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsAPI, cartAPI, ANIMAL_TYPES } from '../../services/storeClubAPI';
import ClubLayout from '../../components/StoreClub/ClubLayout';
import LauraChat from '../../components/StoreClub/LauraChat';

interface Product {
  id: number;
  sku: string;
  name: string;
  brand: string;
  category: string;
  category_display: string;
  product_line: string;
  size_lbs: number;
  price: string;
  is_byoc: boolean;
  stock_status: string;
  kcal_per_cup?: number;
  animal_type?: string;
  target_animal?: string;
  default_form?: string;
}

interface BYOCBin {
  id: number;
  name: string;
  feed_name: string;
  formula_name: string;
  current_lbs: number;
  capacity_lbs: number;
  percentage_full: number;
  price_per_lb: string;
  status: string;
}

interface CartItem {
  id: number;
  product: Product;
  quantity: number;
  line_total: string;
}

interface Cart {
  items: CartItem[];
  subtotal: string;
  locker_fee: string;
  total: string;
  item_count: number;
}

const CATEGORIES = [
  { value: 'all', label: 'All' },
  { value: 'dog_food', label: 'Dog Food' },
  { value: 'cat_food', label: 'Cat Food' },
  { value: 'cat_litter', label: 'Cat Litter' },
  { value: 'wild_bird', label: 'Wild Bird' },
  { value: 'feed_bag', label: 'Feed Bags (40#)' },
  { value: 'byoc', label: 'BYOC Bins' },
];

// Animal type filters for feed bags - maps to FeedFormula.animal_type
const ANIMAL_TYPE_FILTERS = [
  { value: 'all', label: 'All Animals' },
  { value: 'POULTRY', label: 'Poultry' },
  { value: 'SWINE', label: 'Swine' },
  { value: 'RUMINANTS', label: 'Goat / Sheep / All Stock' },
  { value: 'HORSE', label: 'Horse' },
  { value: 'SPECIAL', label: 'Rabbit' },
];

const ClubShop: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [byocBins, setBYOCBins] = useState<BYOCBin[]>([]);
  const [cart, setCart] = useState<Cart | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAnimalType, setSelectedAnimalType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState<number | null>(null);

  useEffect(() => {
    loadProducts();
    loadBYOCBins();
    loadCart();
  }, []);

  const loadProducts = async () => {
    try {
      const data = await productsAPI.getAll();
      console.log('[ClubShop] Raw products from API:', data?.length, 'products');
      console.log('[ClubShop] Sample product:', data?.[0]);

      // Debug: Log unique categories in the data
      const categories = Array.from(new Set(data?.map((p: Product) => p.category) || []));
      console.log('[ClubShop] Unique categories in API response:', categories);

      const feedBags = data?.filter((p: Product) => p.category === 'feed_bag') || [];
      console.log('[ClubShop] Feed bag products:', feedBags.length);
      if (feedBags.length === 0 && data?.length > 0) {
        console.log('[ClubShop] WARNING: No feed_bag category found. First 5 products:', data.slice(0, 5).map((p: Product) => ({ name: p.name, category: p.category })));
      }

      // Filter out BYOC bulk products (category='byoc') - they're shown separately in bins view
      // Keep feed bags (category='feed_bag') - they're purchasable
      const filtered = data.filter((p: Product) => p.category !== 'byoc');
      console.log('[ClubShop] Products after BYOC filter:', filtered?.length);
      setProducts(filtered);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBYOCBins = async () => {
    try {
      const data = await productsAPI.getBYOCStock();
      setBYOCBins(data);
    } catch (error) {
      console.error('Failed to load BYOC bins:', error);
    }
  };

  const loadCart = async () => {
    try {
      const data = await cartAPI.get();
      setCart(data);
    } catch (error) {
      console.error('Failed to load cart:', error);
    }
  };

  const handleAddToCart = async (productId: number) => {
    setAddingToCart(productId);
    try {
      await cartAPI.addItem(productId, 1);
      await loadCart();
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setAddingToCart(null);
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    // Reset animal type filter when changing categories
    if (category !== 'feed_bag') {
      setSelectedAnimalType('all');
    }
  };

  const filteredProducts = products.filter((product) => {
    // Products already filtered in loadProducts to exclude BYOC bulk
    // When 'byoc' category selected, show nothing in products (bins show instead)
    if (selectedCategory === 'byoc') return false;
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.brand && product.brand.toLowerCase().includes(searchQuery.toLowerCase()));

    // Animal type filter only applies to feed_bag category
    let matchesAnimalType = true;
    if (selectedCategory === 'feed_bag' && selectedAnimalType !== 'all') {
      // Only filter by animal type if we're in feed_bag category AND a specific animal is selected
      // Handle null/undefined animal_type gracefully
      matchesAnimalType = product.animal_type === selectedAnimalType;
    }

    return matchesCategory && matchesSearch && matchesAnimalType;
  });

  // Debug logging for filters
  console.log('[ClubShop] Filter state:', {
    selectedCategory,
    selectedAnimalType,
    totalProducts: products.length,
    filteredCount: filteredProducts.length,
    feedBagCount: products.filter(p => p.category === 'feed_bag').length
  });

  const filteredBYOCBins = byocBins.filter((bin) => {
    if (selectedCategory !== 'all' && selectedCategory !== 'byoc') return false;
    const matchesSearch =
      searchQuery === '' ||
      bin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bin.feed_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const key = `${product.brand || 'Other'} - ${product.product_line || product.category_display}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(product);
    return acc;
  }, {} as Record<string, Product[]>);

  const getStockColor = (percentage: number) => {
    if (percentage >= 50) return '#4caf50';
    if (percentage >= 25) return '#ff9800';
    return '#f44336';
  };

  const getStockLabel = (percentage: number) => {
    if (percentage >= 50) return 'In Stock';
    if (percentage >= 25) return 'Low Stock';
    if (percentage > 0) return 'Very Low';
    return 'Empty';
  };

  if (loading) {
    return (
      <ClubLayout>
        <div style={styles.loading}>Loading products...</div>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout>
      <LauraChat message="Welcome to the Shop! Browse our products below, or check BYOC bin levels for in-store pickup." />

      <div style={styles.content}>
        <div style={styles.searchBar}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search products..."
            style={styles.searchInput}
          />
        </div>

        <div style={styles.categories}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              style={{
                ...styles.categoryButton,
                backgroundColor: selectedCategory === cat.value ? '#508590' : '#fff',
                color: selectedCategory === cat.value ? '#fff' : '#333',
              }}
              onClick={() => handleCategoryChange(cat.value)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Animal Type Filters - Only show when Feed Bags category is selected */}
        {selectedCategory === 'feed_bag' && (
          <div style={styles.animalTypeFilters}>
            <span style={styles.filterLabel}>Filter by animal:</span>
            <div style={styles.animalTypeChips}>
              {ANIMAL_TYPE_FILTERS.map((type) => (
                <button
                  key={type.value}
                  style={{
                    ...styles.animalTypeChip,
                    backgroundColor: selectedAnimalType === type.value ? '#2D5A27' : '#f5f5f5',
                    color: selectedAnimalType === type.value ? '#fff' : '#666',
                  }}
                  onClick={() => setSelectedAnimalType(type.value)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* BYOC Bins Section */}
        {filteredBYOCBins.length > 0 && (
          <div style={styles.productGroup}>
            <h3 style={styles.groupTitle}>BYOC - Bring Your Own Container</h3>
            <p style={styles.byocInfo}>
              In-store only. Bring your containers and fill up with fresh feed!
            </p>
            <div style={styles.byocGrid}>
              {filteredBYOCBins.map((bin) => (
                <div key={bin.id} style={styles.byocCard}>
                  <div style={styles.byocHeader}>
                    <span style={styles.byocName}>{bin.name}</span>
                    <span
                      style={{
                        ...styles.stockBadge,
                        backgroundColor: `${getStockColor(bin.percentage_full)}20`,
                        color: getStockColor(bin.percentage_full),
                      }}
                    >
                      {getStockLabel(bin.percentage_full)}
                    </span>
                  </div>
                  <span style={styles.byocFeed}>{bin.feed_name}</span>
                  <div style={styles.progressContainer}>
                    <div style={styles.progressBar}>
                      <div
                        style={{
                          ...styles.progressFill,
                          width: `${bin.percentage_full}%`,
                          backgroundColor: getStockColor(bin.percentage_full),
                        }}
                      />
                    </div>
                    <span style={styles.progressText}>{bin.percentage_full}%</span>
                  </div>
                  <div style={styles.byocFooter}>
                    <span style={styles.byocPrice}>${bin.price_per_lb || '0.00'}/lb</span>
                    <span style={styles.byocStock}>{Math.round(bin.current_lbs)} / {bin.capacity_lbs} lbs</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Regular Products */}
        {filteredProducts.length === 0 && selectedCategory !== 'byoc' && (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>
              {selectedCategory === 'feed_bag'
                ? 'No feed bags found. Run the setup command to create products.'
                : 'No products found matching your filters.'}
            </p>
            <p style={styles.emptySubtext}>
              Total products loaded: {products.length}
            </p>
          </div>
        )}
        {Object.entries(groupedProducts).map(([group, groupProducts]) => (
          <div key={group} style={styles.productGroup}>
            <h3 style={styles.groupTitle}>{group}</h3>
            <div style={styles.productGrid}>
              {groupProducts.map((product) => (
                <div key={product.id} style={styles.productCard}>
                  <div style={styles.productInfo}>
                    <div style={styles.productHeader}>
                      <span style={styles.productName}>{product.name}</span>
                      <span style={styles.productSize}>
                        {product.size_lbs >= 1 ? `${Math.round(product.size_lbs)}#` : `${product.size_lbs}lb`}
                      </span>
                    </div>
                    <span style={styles.productPrice}>${product.price}</span>
                  </div>
                  <button
                    style={{
                      ...styles.addButton,
                      opacity: addingToCart === product.id ? 0.7 : 1,
                    }}
                    onClick={() => handleAddToCart(product.id)}
                    disabled={addingToCart === product.id}
                  >
                    {addingToCart === product.id ? '...' : '+'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {cart && cart.item_count > 0 && (
          <div style={styles.cartBar} onClick={() => navigate('/club/cart')}>
            <div style={styles.cartInfo}>
              <span style={styles.cartCount}>{cart.item_count} items</span>
              <span style={styles.cartTotal}>${cart.total}</span>
            </div>
            <span style={styles.cartAction}>View Cart →</span>
          </div>
        )}
      </div>
    </ClubLayout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  content: {
    padding: '1rem',
    paddingBottom: '8rem', // Extra space for cart bar
    maxWidth: '600px',
    margin: '0 auto',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#666',
    fontSize: '1.125rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  emptyText: {
    color: '#666',
    fontSize: '1rem',
    margin: '0 0 0.5rem 0',
  },
  emptySubtext: {
    color: '#999',
    fontSize: '0.75rem',
    margin: 0,
  },
  searchBar: {
    marginBottom: '1rem',
  },
  searchInput: {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: "'Open Sans', sans-serif",
  },
  categories: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    paddingBottom: '0.5rem',
  },
  categoryButton: {
    padding: '0.5rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '20px',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s',
    fontFamily: "'Open Sans', sans-serif",
  },
  animalTypeFilters: {
    marginBottom: '1rem',
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  filterLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'block',
    marginBottom: '0.5rem',
  },
  animalTypeChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.375rem',
  },
  animalTypeChip: {
    padding: '0.375rem 0.75rem',
    border: 'none',
    borderRadius: '16px',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: "'Open Sans', sans-serif",
  },
  productGroup: {
    marginBottom: '1.5rem',
  },
  groupTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#508590',
    margin: '0 0 0.75rem 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  productGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  productCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  productInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  productHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0.5rem',
  },
  productName: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.9375rem',
  },
  productSize: {
    fontSize: '0.8125rem',
    color: '#666',
  },
  byocBadge: {
    display: 'inline-block',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: '0.125rem 0.375rem',
    borderRadius: '4px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    alignSelf: 'flex-start',
  },
  productPrice: {
    fontWeight: 600,
    color: '#E73C3E',
    fontSize: '1rem',
  },
  addButton: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#508590',
    color: '#fff',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cartBar: {
    position: 'fixed',
    bottom: '70px',
    left: '1rem',
    right: '1rem',
    maxWidth: '568px',
    margin: '0 auto',
    backgroundColor: '#E73C3E',
    color: '#fff',
    padding: '1rem 1.25rem',
    borderRadius: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    cursor: 'pointer',
  },
  cartInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  cartCount: {
    fontSize: '0.8125rem',
    opacity: 0.9,
  },
  cartTotal: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
  },
  cartAction: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  byocInfo: {
    fontSize: '0.8125rem',
    color: '#666',
    margin: '0 0 1rem 0',
    fontStyle: 'italic',
  },
  byocGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  byocCard: {
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  byocHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.25rem',
  },
  byocName: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.9375rem',
  },
  stockBadge: {
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.6875rem',
    fontWeight: 600,
  },
  byocFeed: {
    fontSize: '0.8125rem',
    color: '#666',
    display: 'block',
    marginBottom: '0.75rem',
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  progressBar: {
    flex: 1,
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#666',
    minWidth: '32px',
    textAlign: 'right',
  },
  byocFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  byocPrice: {
    fontWeight: 600,
    color: '#E73C3E',
    fontSize: '0.9375rem',
  },
  byocStock: {
    fontSize: '0.75rem',
    color: '#999',
  },
};

export default ClubShop;
